// ============================================================================
// RÉFÉRENTIELS → VOCABULAIRE — l'onglet où le COMMERÇANT pose son mot pour chaque champ d'article.
//
// ⚠️ UN ONGLET DES RÉFÉRENTIELS, PLUS UNE ENTRÉE DE RAIL (maquette `fgTabs` : vocab, alignée le 2026-10-03) : un mot n'est
//    pas un réglage de caisse, c'est une liste de l'enseigne — le seul onglet ici qui dépend de la LANGUE du compte.
//
// UTILITÉ : « Saison » chez le prêt-à-porter, « Millésime » chez le caviste — ça, c'est la Console qui
// le fait descendre (`vocab_default.<champ>.<langue>`, le mot du métier). Ici, le patron qui veut lire
// « Pitouf » l'écrit, dans SA langue, et tous les écrans du back-office puis les caisses le suivent.
// C'est le troisième étage de la cascade du contrat (`@orgazone/sync-contract`, `resolveVocabulary`) :
// surcharge du commerçant → mot du métier → mot de la plateforme. Ce qu'il efface retombe sur l'étage
// du dessous ; il n'écrit jamais `vocab_default.*`, qui appartient à la propagation.
//
// ⚠️ CET ÉCRAN NE LIT PAS `fieldLabel()` POUR NOMMER SES LIGNES, et ce n'est pas un site à migrer :
//    il montre le dictionnaire LUI-MÊME. La colonne « Champ » porte le mot de la PLATEFORME parce que
//    c'est l'identité stable de la ligne — nommée par le mot du magasin, la ligne « Saison » deviendrait
//    « Pitouf » dès la surcharge posée, et le commerçant ne retrouverait plus quel champ il a renommé.
//    Aucun mot n'est écrit en dur ici : tous viennent du serveur (`platform`, `tradeDefault`), lus au
//    rendu — l'audit `audit-libelles-en-dur` n'y compte rien, à raison.
// ⚠️ « Affiché » EST RECALCULÉ LOCALEMENT PENDANT LA SAISIE, avec la MÊME règle que le résolveur du
//    contrat (`effectiveWord`) — pour que la colonne réagisse à chaque frappe sans aller-retour serveur.
//    Une règle écrite deux fois diverge (Forme A) : la parité est TENUE par `vocabulaire.test.ts`, qui
//    confronte `effectiveWord` à `resolveVocabulary` sur toutes les combinaisons de vide, de blanc et
//    de mot. Et au repos, c'est `effective` — la vérité du serveur — qui s'affiche, jamais le calcul local.
// ⚠️ PAS DE `DataTable` : un dictionnaire d'une ligne fixe par champ, saisie en ligne, ni tri, ni filtre,
//    ni page — le tableau riche de la charte sert les listes bornées côté serveur. Un `<table>` dans un
//    conteneur qui défile, comme les tableaux de détail des inventaires.
// ============================================================================
import { useCallback, useEffect, useMemo, useState } from 'react';
import { EmptyState, Icon, showToast, Trans } from '@orgazone/ui-web';
import { firstUsableWord, isUsableWord, type VocabularyFieldKey } from '@orgazone/sync-contract';
import { api, type StoreVocabularyField, type StoreVocabularyView } from '../api';
import { setCurrentVocabulary } from '../vocabulary';
import { currentLang } from '../lang';
import { t } from '../i18n';

/** Ce que le commerçant a tapé, par champ — `undefined` = la ligne n'a pas été touchée. */
export type TypedOverrides = Partial<Record<VocabularyFieldKey, string>>;

/**
 * LE MOT AFFICHÉ pour un champ pendant la frappe — LA cascade du contrat, pas une seconde.
 * ⚠️ `firstUsableWord` est celle que `resolveVocabulary` applique côté serveur : la surcharge si elle
 *    est non blanche, sinon le mot du métier, sinon la plateforme. Le test de parité la confronte à
 *    `resolveVocabulary` pour qu'aucune des deux ne dérive sans que l'autre le voie.
 */
export function effectiveWord(override: string | null | undefined, tradeDefault: string | null | undefined, platform: string): string {
  return firstUsableWord(platform, override, tradeDefault);
}

/** La surcharge d'ORIGINE d'une ligne, telle que le champ de saisie la montre : jamais `null`. */
const originOf = (field: StoreVocabularyField | undefined): string => field?.override ?? '';

/**
 * LE BROUILLON : ce qui a VRAIMENT changé par rapport à la vue lue.
 * ⚠️ Revenir à la valeur d'origine RETIRE la clé — on n'écrit pas au journal un mot que personne n'a
 *    changé (même règle que `poser` de l'écran Paramètres). Une valeur blanche compte comme vide : c'est
 *    ainsi que le serveur la lit, et c'est ce qui permet de RETIRER une surcharge en effaçant le champ.
 * ⚠️ Une clé absente de la vue n'est PAS écartée : elle ne peut pas venir de l'écran, et si elle
 *    arrivait quand même, c'est au serveur de la refuser avec son motif — pas à ce filtre de l'avaler.
 */
export function draftOf(view: Pick<StoreVocabularyView, 'fields'>, typed: TypedOverrides): TypedOverrides {
  const byKey = new Map(view.fields.map((f) => [f.key, f] as const));
  const draft: TypedOverrides = {};
  for (const [key, value] of Object.entries(typed) as [VocabularyFieldKey, string | undefined][]) {
    if (value === undefined) continue;
    const next = value.trim();
    if (next === originOf(byKey.get(key)).trim()) continue;
    draft[key] = next;
  }
  return draft;
}

/**
 * LE NOM D'UNE LANGUE, dans la langue de l'interface (« français », « French ») — ou son code quand l'environnement ne
 * sait pas la nommer : on dit ce qu'on sait, jamais un nom deviné.
 */
export function languageName(lang: string, uiLang: string): string {
  try {
    return new Intl.DisplayNames([uiLang], { type: 'language' }).of(lang) ?? lang;
  } catch {
    // ⚠️ Best-effort assumé : une étiquette de langue malformée fait lever `Intl` — l'onglet ne tombe pas pour un nom.
    return lang;
  }
}

/** L'onglet Vocabulaire des Référentiels : le dictionnaire du magasin, ligne par champ d'article. */
export function VocabularyTab() {
  const [view, setView] = useState<StoreVocabularyView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [typed, setTyped] = useState<TypedOverrides>({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.storeVocabulary()
      .then((v) => { setView(v); setError(null); })
      .catch((e) => setError((e as Error).message));
  }, []);
  useEffect(() => { load(); }, [load]);

  const draft = useMemo<TypedOverrides>(() => (view ? draftOf(view, typed) : {}), [view, typed]);
  const changed = Object.keys(draft).length;

  const save = async () => {
    if (!changed || busy || !view) return;
    setBusy(true);
    try {
      const r = await api.saveStoreVocabulary(draft);
      // ⚠️ Les mots se posent AVANT que la vue ne soit commitée — comme au contexte magasin : le reste
      //    de la SPA lit le singleton au rendu, et un écran rendu entre les deux dirait l'ancien mot.
      setCurrentVocabulary(r.vocabulary);
      setView({ lang: r.lang, fields: r.fields });
      setTyped({});
      showToast(t('Vocabulaire enregistré — il descendra aux caisses au prochain cycle.'), 'ok', 8000);
    } catch (e) {
      showToast(t('Enregistrement refusé : {v}', { v: (e as Error).message }), 'err', 10000);
    } finally { setBusy(false); }
  };

  const overridden = view ? view.fields.filter((f) => isUsableWord(f.override)).length : 0;

  // La langue dont le dictionnaire montre les mots : celle que le serveur a lue, sinon celle de l'interface.
  const lang = view?.lang ?? currentLang();

  return (
    <div>
      {/* L'en-tête de l'onglet — relevé de la maquette : la règle en une phrase, puis l'état et le geste. */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 260, fontSize: 'var(--t-body)', color: 'var(--ink-500)', lineHeight: 1.55 }}>
          <Trans k='Le mot que vous choisissez remplace celui du métier, <1>dans votre langue seulement</1> ({lang} — Mon compte). Vide, il retombe sur le mot du métier descendu par Orgazone ; s’il n’y en a pas, sur celui de la plateforme. Il descend aux caisses au prochain cycle.'
            vars={{ lang: languageName(lang, currentLang()) }}
            parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} />
        </div>
        {changed > 0 && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 30, padding: '0 11px', borderRadius: 'var(--r-pill)', background: 'var(--amber-bg)', color: 'var(--amber-fg)', fontSize: 'var(--t-note)', fontWeight: 700, whiteSpace: 'nowrap' }}>
            <span style={{ width: 7, height: 7, borderRadius: 'var(--r-pill)', background: 'var(--amber)' }} />
            {t('{n} modification non enregistrée', { n: changed })}
          </span>
        )}
        <button onClick={() => void save()} disabled={!changed || busy}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 7, height: 38, padding: '0 16px', border: 'none',
            borderRadius: 'var(--r-ctrl)', background: changed ? 'var(--teal-500)' : 'var(--ink-200)', color: changed ? '#fff' : 'var(--ink-400)',
            fontSize: 'var(--t-lead)', fontWeight: 700, whiteSpace: 'nowrap', cursor: changed && !busy ? 'pointer' : 'not-allowed',
          }}>
          <Icon name="save" size={15} stroke={1.9} />
          {busy ? t('Enregistrement…') : t('Enregistrer')}
        </button>
      </div>

      {error != null ? (
        <EmptyState icon="alert" title={t('Vocabulaire indisponible')} note={error} />
      ) : view === null ? (
        <div style={{ padding: 34, textAlign: 'center', color: 'var(--ink-500)', fontSize: 'var(--t-body)' }}>{t('Chargement…')}</div>
      ) : view.fields.length === 0 ? (
        <EmptyState icon="alert" title={t('Aucun champ')} note={t('Le serveur n’a rendu aucun champ d’article à nommer : le catalogue du contrat est vide de son côté.')} />
      ) : (
        <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', background: 'var(--paper)', overflow: 'hidden' }}>
          <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--ink-100)', fontSize: 'var(--t-note)', color: 'var(--ink-500)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <span><Trans k='{n} champ · langue <1>{lang}</1>' vars={{ n: view.fields.length, lang: view.lang }} parts={[(x) => <code style={{ fontFamily: 'var(--font-mono)' }}>{x}</code>]} /></span>
            <span>{overridden === 0 ? t('aucun mot surchargé dans ce magasin') : t('{n} mot surchargé dans ce magasin', { n: overridden })}</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--t-lead)' }}>
              <thead>
                <tr style={{ background: 'var(--ink-50)' }}>
                  <Th>{t('Champ')}</Th>
                  <Th>{t('Mot du métier')}</Th>
                  <Th>{t('Votre mot')}</Th>
                  <Th>{t('Affiché')}</Th>
                </tr>
              </thead>
              <tbody>
                {view.fields.map((f) => (
                  <Ligne key={f.key} field={f} typed={typed[f.key]} inDraft={f.key in draft}
                    onChange={(v) => setTyped((prev) => ({ ...prev, [f.key]: v }))} />
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ padding: '10px 16px', background: 'var(--ink-50)', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', lineHeight: 1.55 }}>
            <Trans k='Trois étages, un seul mot affiché : <1>votre mot</1> s’il est renseigné, sinon celui du <2>métier</2> (descendu par Orgazone selon votre secteur), sinon celui de la <3>plateforme</3>. Une case effacée retire la surcharge — elle ne met pas un vide.'
              parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} />
          </div>
        </div>
      )}
    </div>
  );
}

/** Un en-tête de colonne — la forme des tableaux de détail du back-office. */
function Th({ children }: { children: string }) {
  return (
    <th scope="col" style={{ padding: '9px 12px', textAlign: 'left', fontSize: 'var(--t-micro)', fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-500)', whiteSpace: 'nowrap' }}>
      {children}
    </th>
  );
}

/** Une ligne du dictionnaire : le champ, le mot du métier en lecture, la saisie, et ce qui en sort. */
function Ligne({ field, typed, inDraft, onChange }: {
  field: StoreVocabularyField; typed: string | undefined; inDraft: boolean; onChange: (v: string) => void;
}) {
  const touched = typed !== undefined;
  // ⚠️ Au repos, la VÉRITÉ DU SERVEUR (`effective`) ; pendant la saisie, le calcul local de la même règle.
  //    Afficher le calcul local au repos aurait masqué une divergence entre les deux — c'est le test de
  //    parité qui la tient, pas cette colonne.
  const shown = touched ? effectiveWord(typed, field.tradeDefault, field.platform) : field.effective;
  const td = { padding: '8px 12px', color: 'var(--ink-700)', verticalAlign: 'middle' as const };
  return (
    <tr style={{ borderTop: '1px solid var(--ink-100)' }}>
      <td style={{ ...td, fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap' }}>
        {field.platform}
        <span style={{ display: 'block', fontSize: 'var(--t-note)', fontWeight: 400, color: 'var(--ink-400)', fontFamily: 'var(--font-mono)' }}>{field.key}</span>
      </td>
      <td style={td}>
        {field.tradeDefault !== null
          ? field.tradeDefault
          /* ⚠️ « — » ≠ vide : rien n'est descendu de la Console pour ce champ, le filet de la plateforme sert. */
          : <span style={{ color: 'var(--ink-300)' }}>—</span>}
      </td>
      <td style={td}>
        <input
          value={typed ?? originOf(field)}
          onChange={(e) => onChange(e.target.value)}
          placeholder={effectiveWord(null, field.tradeDefault, field.platform)}
          aria-label={t('Votre mot pour « {v} »', { v: field.platform })}
          style={{
            height: 34, padding: '0 12px', borderRadius: 'var(--r-ctrl)', width: 220, maxWidth: '100%',
            background: 'var(--canvas)', border: `1px solid ${inDraft ? 'var(--amber)' : 'var(--ink-200)'}`,
            fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-800)', outline: 'none', boxSizing: 'border-box',
          }} />
      </td>
      <td style={{ ...td, fontWeight: 700, color: inDraft ? 'var(--amber-fg)' : 'var(--ink-900)' }}>{shown}</td>
    </tr>
  );
}
